import type { QuickAddCursorState } from "@/pages/canvas/context/CanvasUiContext";
import type { WidgetPreset } from "@/pages/canvas/nodes/widgets";
import { createCascadePlacementState } from "@/pages/canvas/utils/canvasInsertCascadePlacement";
import type { FileRecord } from "@cohesive/api-client";
import type { Entity } from "@cohesive/ui-components";
import { getEntityName } from "@cohesive/ui-components";
import { useCallback, useEffect, useMemo, useRef } from "react";
import toast from "react-hot-toast";
import { useCanvasFileIngest } from "./useCanvasFileIngest";
import { useCanvasWidgetQuickPlace } from "./useCanvasWidgetQuickPlace";
import { useCreateDocOnCanvas } from "./useCreateDocOnCanvas";
import { useFileRecordCreateFromURL } from "./useFileRecordCreateFromURL";

/** Toolbar and left rail share this profile exactly. */
export type CanvasInsertPlacementProfile = "chrome" | "contextMenu";

export type CanvasInsertSource = "toolbar" | "rail" | "paneContextMenu";

export interface CanvasInsertMenuContext {
  source: CanvasInsertSource;
  placement: CanvasInsertPlacementProfile;
  canvasId: string | undefined;
  cursor?: QuickAddCursorState | null;
}

export interface CanvasInsertMenuActions {
  onAddWidget: (preset: WidgetPreset) => void;
  onAddLinkSubmit: (url: string) => void;
  onNewNote: () => void;
  onNewDocument: () => void;
  onUploadFile: () => void;
  /** Pick a Library file and place it as a reference (A2). Absent while the
   *  canvas's workspace is unknown. */
  onAddFromLibrary?: {
    workspaceId: string;
    onPick: (record: FileRecord, at?: { x: number; y: number }) => void;
  };
  onSelectEntity: (
    entity: Entity,
    flowPosition: { x: number; y: number },
  ) => void;
  onStartQuickChat?: () => void;
  onStartQuickVoice?: () => void;
  onPaste?: () => void;
}

export interface ChromeInsertHandlers {
  onNewNote: () => void;
  onNewDocument: () => void;
  onUploadFile: () => void;
  onAddLinkSubmit: (url: string) => void;
  onAddWidget: (preset: WidgetPreset) => void;
  onSelectEntityViewport: (entity: Entity) => void;
}

export interface UseCanvasInsertMenuActionsParams {
  context: CanvasInsertMenuContext;
  onClose?: () => void;
  chrome: ChromeInsertHandlers;
  startTextNotePlacement: (screenPos?: { x: number; y: number } | null) => void;
  onSelectEntityAtPosition: (
    entity: Entity,
    flowPosition: { x: number; y: number },
  ) => void;
  onPasteAtPosition?: (flowPosition: { x: number; y: number }) => void;
  onStartQuickChat?: (screenPosition: { x: number; y: number }) => void;
  onStartQuickVoice?: (screenPosition: { x: number; y: number }) => void;
  /** Chrome profile: pick chat/voice, then click the canvas to place. */
  beginDeferredQuickCompose?: (mode: "quick-chat" | "quick-voice") => void;
}

export function useCanvasInsertMenuActions({
  context,
  onClose,
  chrome,
  startTextNotePlacement,
  onSelectEntityAtPosition,
  onPasteAtPosition,
  onStartQuickChat,
  onStartQuickVoice,
  beginDeferredQuickCompose,
}: UseCanvasInsertMenuActionsParams) {
  const { placement, source, canvasId, cursor } = context;
  const { placeWidget } = useCanvasWidgetQuickPlace();
  const createDoc = useCreateDocOnCanvas();
  const ingestURL = useFileRecordCreateFromURL();
  const { ingestFiles } = useCanvasFileIngest(canvasId);

  const cascadeRef = useRef<ReturnType<
    typeof createCascadePlacementState
  > | null>(null);

  useEffect(() => {
    if (!cursor || placement !== "contextMenu") {
      cascadeRef.current = null;
      return;
    }
    cascadeRef.current = createCascadePlacementState(
      cursor.flowPosition,
      cursor.clientX,
    );
  }, [cursor, placement]);

  const getCascade = useCallback(() => {
    if (!cascadeRef.current || !cursor) {
      throw new Error("contextMenu cascade requires cursor");
    }
    return cascadeRef.current;
  }, [cursor]);

  const closeIfNeeded = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const actions = useMemo((): CanvasInsertMenuActions => {
    if (placement === "chrome") {
      return {
        onAddWidget: chrome.onAddWidget,
        onAddLinkSubmit: chrome.onAddLinkSubmit,
        onNewNote: chrome.onNewNote,
        onNewDocument: chrome.onNewDocument,
        onUploadFile: chrome.onUploadFile,
        onSelectEntity: (entity) => {
          chrome.onSelectEntityViewport(entity);
        },
        onStartQuickChat:
          beginDeferredQuickCompose && onStartQuickChat
            ? () => {
                closeIfNeeded();
                beginDeferredQuickCompose("quick-chat");
              }
            : undefined,
        onStartQuickVoice:
          beginDeferredQuickCompose && onStartQuickVoice
            ? () => {
                closeIfNeeded();
                beginDeferredQuickCompose("quick-voice");
              }
            : undefined,
        onPaste: undefined,
      };
    }

    if (!cursor) {
      return {
        onAddWidget: () => {},
        onAddLinkSubmit: () => {},
        onNewNote: () => {},
        onNewDocument: () => {},
        onUploadFile: () => {},
        onSelectEntity: () => {},
      };
    }

    const cascade = getCascade;

    return {
      onAddWidget: (preset) => {
        placeWidget(preset, cascade().snapCursor());
        closeIfNeeded();
      },
      onAddLinkSubmit: (url) => {
        ingestURL({ sourceUrl: url, position: cascade().snapCursor() });
        closeIfNeeded();
      },
      onNewNote: () => {
        startTextNotePlacement({ x: cursor.clientX, y: cursor.clientY });
        closeIfNeeded();
      },
      onNewDocument: () => {
        createDoc(cascade().snapCursor());
        closeIfNeeded();
      },
      onUploadFile: () => {
        // File picker is opened by CanvasInsertMenu; ingest runs in ingestContextMenuFiles.
      },
      onSelectEntity: (entity, flowPosition) => {
        onSelectEntityAtPosition(entity, flowPosition);
        toast.success(`Added ${getEntityName(entity)} to canvas`, {
          duration: 2000,
          icon: "✓",
        });
        closeIfNeeded();
      },
      onStartQuickChat: onStartQuickChat
        ? () => {
            closeIfNeeded();
            onStartQuickChat({ x: cursor.clientX, y: cursor.clientY });
          }
        : undefined,
      onStartQuickVoice: onStartQuickVoice
        ? () => {
            closeIfNeeded();
            onStartQuickVoice({ x: cursor.clientX, y: cursor.clientY });
          }
        : undefined,
      onPaste: onPasteAtPosition
        ? () => {
            onPasteAtPosition(cascade().snapCursor());
            closeIfNeeded();
          }
        : undefined,
    };
  }, [
    placement,
    source,
    cursor,
    chrome,
    closeIfNeeded,
    onStartQuickChat,
    onStartQuickVoice,
    beginDeferredQuickCompose,
    getCascade,
    placeWidget,
    ingestURL,
    startTextNotePlacement,
    createDoc,
    onSelectEntityAtPosition,
    onPasteAtPosition,
  ]);

  const handleContextMenuEntitySelect = useCallback(
    (entity: Entity) => {
      if (placement !== "contextMenu" || !cursor) return;
      const cascade = getCascade();
      const flowPos = cascade.next();
      actions.onSelectEntity(entity, flowPos);
    },
    [placement, cursor, getCascade, actions],
  );

  const ingestContextMenuFiles = useCallback(
    async (files: File[]) => {
      if (placement !== "contextMenu") return;
      const cascade = getCascade();
      await ingestFiles(files, {
        placement: { strategy: "cascade", next: () => cascade.next() },
      });
      closeIfNeeded();
    },
    [placement, getCascade, ingestFiles, closeIfNeeded],
  );

  const handleContextMenuEntitySelectWithDims = useCallback(
    (entity: Entity) => {
      if (placement === "contextMenu") {
        handleContextMenuEntitySelect(entity);
        return;
      }
      chrome.onSelectEntityViewport(entity);
      toast.success(`Added ${getEntityName(entity)} to canvas`, {
        duration: 2000,
        icon: "✓",
      });
    },
    [placement, handleContextMenuEntitySelect, chrome],
  );

  return {
    actions,
    ingestContextMenuFiles,
    handleIntegrationEntitySelect: handleContextMenuEntitySelectWithDims,
  };
}
